import React, { useEffect, useState } from 'react';
import { Checkbox, Toggle, TooltipTrigger, Text, sparkToast, useReadOnly } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { useAuth } from '@/features/auth/AuthContext';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';

/**
 * "Code detected" panel for the Data Overview card. Persists user feedback via
 * `update-dataset-code-status` and falls back to optimistic UI on the
 * provided `onCodeChange` callback.
 */
const CodeSection = ({ isCode, onCodeChange, currentUsecase, isBenchmark = false }) => {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const [checked, setChecked] = useState(Boolean(isCode));
    const isViewer = role === 'Viewer';

    useEffect(() => {
        setChecked(Boolean(isCode));
    }, [isCode, currentUsecase?._id, isBenchmark]);

    const handleChange = (nextChecked) => {
        if (!currentUsecase) return;
        setChecked(nextChecked);
        if (onCodeChange) onCodeChange(nextChecked, isBenchmark);
        const datasets = isBenchmark ? currentUsecase.benchmarks : currentUsecase.datasets;
        data_from_server_post(
            'update-dataset-code-status',
            {
                client_id: currentUsecase.client_id,
                datasets,
                is_benchmark: isBenchmark,
                is_code: nextChecked,
                llm_usecase_id: currentUsecase._id,
            },
            (response) => {
                if (!response?.success) {
                    setChecked(!nextChecked);
                    if (onCodeChange) onCodeChange(!nextChecked, isBenchmark);
                    sparkToast.error(response?.message || 'Failed to update code status');
                }
            },
            (error) => {
                setChecked(!nextChecked);
                if (onCodeChange) onCodeChange(!nextChecked, isBenchmark);
                sparkToast.error(typeof error === 'string' ? error : 'Failed to update code status');
            }
        );
    };

    if (checked) {
        return (
            <AnalysisPropertyRow
                icon="codeblock"
                label="Code detected"
                description={
                    <TooltipTrigger text="Code examples are detected in the dataset" placement="top">
                        <Text as="span" variant="detail-regular" color="secondary">
                            All code
                        </Text>
                    </TooltipTrigger>
                }
                trailing={(
                    <Toggle
                        size="small"
                        active={checked}
                        disabled={readOnly || isViewer}
                        onChange={(e) => handleChange(e.target.checked)}
                    />
                )}
            />
        );
    }

    return (
        <AnalysisPropertyRow
            icon="codeblock"
            label="Code detected"
            value="No code detected in data"
        >
            <Checkbox
                className="data-overview-detail-checkbox"
                label="Check this box if there is code in data added"
                status="inactive"
                disabled={readOnly || isViewer}
                onChange={(e) => handleChange(e.target.checked)}
            />
        </AnalysisPropertyRow>
    );
};

export default CodeSection;
